import {
  TrainingCenterDisplayLanguage,
  TrainingCenterTranslated,
} from "../../messages/TrainingCenter";
import { List } from "../../utils/List";

export interface TrainingCenterLanguagePreference {
  preferredLanguage: TrainingCenterDisplayLanguage | null;
}

export const FALLBACK_LANGUAGE = TrainingCenterDisplayLanguage.wrap("English");

export function trainingCenterLanguagePreferenceFromLang(
  language: TrainingCenterDisplayLanguage
): TrainingCenterLanguagePreference {
  return {
    preferredLanguage: language,
  };
}

export function trainingCenterLanguagePreferenceGetLanguage(
  pref: TrainingCenterLanguagePreference
): TrainingCenterDisplayLanguage {
  if (pref.preferredLanguage !== null) {
    return pref.preferredLanguage;
  } else {
    return FALLBACK_LANGUAGE;
  }
}

export function lookupTranslationOptionalWithLanguage<S, T>(
  languagePreference: TrainingCenterLanguagePreference,
  translations: List<TrainingCenterTranslated<T>>,
  findFn: (item: T) => S | undefined
): TrainingCenterTranslated<S> | null {
  const found =
    languagePreference.preferredLanguage !== null
      ? translations.find(
          (translation) => translation.language === languagePreference.preferredLanguage
        )
      : undefined;

  if (found !== undefined) {
    const result = findFn(found.data);
    if (result !== undefined) {
      return {
        language: found.language,
        data: result,
      };
    }
  }

  const fallbackFound = translations.find(
    (translation) => translation.language === FALLBACK_LANGUAGE
  );

  if (fallbackFound !== undefined) {
    const result = findFn(fallbackFound.data);
    if (result !== undefined) {
      return {
        language: fallbackFound.language,
        data: result,
      };
    }
  }

  return null;
}

export function lookupTranslationRequiredWithLanguage<S, T>(
  languagePreference: TrainingCenterLanguagePreference,
  translations: List<TrainingCenterTranslated<T>>,
  findFn: (item: T) => S | undefined
): TrainingCenterTranslated<S> {
  const result = lookupTranslationOptionalWithLanguage(languagePreference, translations, findFn);

  if (result === null) {
    throw new Error(`Required data not found in translations: ${JSON.stringify(translations)}`);
  }

  return result;
}

export function lookupTranslationOptional<S, T>(
  languagePreference: TrainingCenterLanguagePreference,
  translations: List<TrainingCenterTranslated<T>>,
  findFn: (item: T) => S | undefined
): S | null {
  const result = lookupTranslationOptionalWithLanguage(languagePreference, translations, findFn);
  if (result === null) {
    return null;
  } else {
    return result.data;
  }
}

export function lookupTranslationRequired<S, T>(
  languagePreference: TrainingCenterLanguagePreference,
  translations: List<TrainingCenterTranslated<T>>,
  findFn: (item: T) => S | undefined
): S {
  return lookupTranslationRequiredWithLanguage(languagePreference, translations, findFn).data;
}

/**
 * Converts a `null` value to `undefined`, otherwise returns the original
 * value
 */
export function nullToUndefined<T>(value: T | null): T | undefined {
  if (value !== null) {
    return value;
  } else {
    return undefined;
  }
}
